/**
 * Auth0 Callback Page
 *
 * This page handles the redirect from Auth0 after authentication.
 * It displays a loading state while the Auth0Provider processes
 * the authorization code and exchanges it for tokens.
 */

import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { AlertCircle } from 'lucide-react'

export default function CallbackPage() {
  const location = useLocation()
  const navigate = useNavigate()

  // Check for error in URL params
  const params = new URLSearchParams(location.search)
  const error = params.get('error')
  const errorDescription = params.get('error_description')

  useEffect(() => {
    // If there's an error, redirect to login after showing message
    if (error) {
      const timer = setTimeout(() => {
        navigate('/login')
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [error, navigate])

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <AlertCircle size={48} className="text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            Authentication Failed
          </h1>
          <p className="text-gray-600 mb-4">
            {errorDescription || 'An error occurred during authentication.'}
          </p>
          <p className="text-sm text-gray-500">
            Redirecting to login...
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="h-8 w-8 mx-auto mb-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
        <h1 className="text-xl font-semibold text-gray-900 mb-2">
          Completing Sign In
        </h1>
        <p className="text-gray-600">
          Please wait while we complete your authentication...
        </p>
      </div>
    </div>
  )
}
